"use client";

import { useState, useEffect } from "react";

const TEAL = "#4FCDC4";
const TEXT = "#EDEDED";
const TEXT_MUTED = "#a0a0a0";
const BG = "#09090b";

function FooterLink({ href, children }) {
  return (
    <a href={href}
      style={{
        color: TEAL,
        textDecoration: "underline",
        textUnderlineOffset: "3px",
      }}
    >{children}</a>
  );
}

function useMedia(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

const COLUMNS = [
  {
    title: "Platform",
    links: [
      { label: "AI Analytics", href: "/platform/ai-analytics" },
      { label: "Answer Engine Insights", href: "/platform/answer-engine-insights" },
      { label: "Answer Engine Optimization", href: "/platform/aeo" },
      { label: "AI Ads", href: "/platform/ai-ads" },
      { label: "AI Automation", href: "/platform/ai-automation" },
    ],
  },
  {
    title: "Integrations",
    links: [
      { label: "ChatGPT Ads", href: "/integrations/chatgpt-ads" },
      { label: "Google Ads", href: "/integrations/google-ads" },
      { label: "Google Search Console", href: "/integrations/google-search-console" },
      { label: "Google Analytics 4", href: "/integrations/google-analytics-4" },
      { label: "MCP Server", href: "/integrations/mcp-server" },
      { label: "Slack", href: "/integrations/slack" },
      { label: "WordPress", href: "/integrations/wordpress" },
    ],
  },
  {
    title: "Free Tools",
    links: [
      { label: "AEO Audit", href: "/tools/aeo-audit" },
      { label: "AI Visibility Checker", href: "/tools/ai-visibility-checker" },
      { label: "AI Bot Access Checker", href: "/tools/ai-bot-checker" },
      { label: "llms.txt Generator", href: "/tools/llms-txt-generator" },
      { label: "ChatGPT Ads Mockup", href: "/tools/chatgpt-ads-mockup" },
      { label: "ChatGPT Ads Pixel Checker", href: "/tools/chatgpt-ads-pixel-checker" },
      { label: "ChatGPT Ads Converter", href: "/tools/google-ads-to-chatgpt-ads-converter" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "AI Marketing Glossary", href: "/glossary" },
      { label: "AI Marketing Framework", href: "/ai-marketing-framework" },
      { label: "AI Marketing Playbook", href: "/playbook" },
      { label: "AI Bot Blocking Index", href: "/research/ai-bot-blocking-index" },
      { label: "Google Ads to ChatGPT Ads Study", href: "/research/google-ads-to-chatgpt-ads-migration-study" },
      { label: "Compare Platforms", href: "/compare" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Editorial Standards", href: "/editorial-standards" },
      { label: "Pricing", href: "/pricing" },
      { label: "Support", href: "/support" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
    ],
  },
];

// Temporarily hidden 2026-07-31 while the company profiles have no posts
// (earned-media outreach in flight; editors click these). Flip to true to
// restore. Schema sameAs references in layout.jsx / facts.jsonld / about /
// llms.txt are intentionally untouched — entity graph must keep them.
const SHOW_SOCIAL = false;

const SOCIAL = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/ai-advisors-app/",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  },
  {
    label: "X (Twitter)",
    href: "https://x.com/aiadvisors_ai",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  },
];

// Oversized brand wordmark that bleeds edge-to-edge below the footer, leaving a
// final branded impression for visitors who scroll to the very bottom. Decorative
// (aria-hidden) — the accessible brand name already lives in the footer above.
function BrandBillboard({ isMobile }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "relative",
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        paddingTop: isMobile ? 8 : 12,
        lineHeight: 1,
        userSelect: "none",
      }}
    >
      <span
        style={{
          fontFamily: "var(--brand)",
          fontWeight: 800,
          fontSize: "clamp(52px, 15vw, 280px)",
          letterSpacing: "-0.03em",
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "flex-start",
          gap: "0.1em",
          marginBottom: isMobile ? "-0.08em" : "-0.12em",
        }}
      >
        <span style={{
          backgroundImage: `linear-gradient(180deg, ${TEAL} 32%, ${BG} 96%)`,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}>
          {"✦"}
        </span>
        <span
          style={{
            backgroundImage: `linear-gradient(180deg, ${TEXT} 32%, ${BG} 96%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          AI-Advisors
        </span>
      </span>
    </div>
  );
}

export default function Footer() {
  const isMobile = useMedia("(max-width: 768px)");

  return (
    <>
    <footer style={{
      borderTop: "1px solid rgba(255,255,255,0.04)",
      padding: isMobile ? "40px 20px 24px" : "56px 32px 24px",
      maxWidth: 1200,
      margin: "0 auto",
    }}>
      <div style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: isMobile ? 40 : 64,
        marginBottom: isMobile ? 32 : 48,
      }}>
        {/* Brand */}
        <div style={{ maxWidth: 280 }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8, marginBottom: 12, color: "inherit", textDecoration: "none" }}>
            <span style={{ color: TEAL, fontSize: isMobile ? 24 : 16, lineHeight: 1, transform: "translateY(-1px)" }}>&#10022;</span>
            <span style={{ fontFamily: "var(--brand)", fontWeight: 700, fontSize: 17, color: TEXT }}>AI-Advisors</span>
          </a>
          <p style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT_MUTED, lineHeight: 1.6, margin: 0 }}>
            <FooterLink href="/platform/ai-analytics">Track</FooterLink>, <FooterLink href="/platform/answer-engine-insights">monitor</FooterLink>, <FooterLink href="/platform/aeo">optimize</FooterLink>, <FooterLink href="/platform/ai-ads">amplify</FooterLink>, and <FooterLink href="/platform/ai-automation">scale</FooterLink> your AI visibility without growing your headcount.
          </p>
          {SHOW_SOCIAL && <div style={{ display: "flex", gap: 14, marginTop: 18 }}>
            {SOCIAL.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                style={{ display: "flex", color: "rgba(255,255,255,0.55)", transition: "color 0.2s" }}
                onMouseOver={(e) => e.currentTarget.style.color = TEXT}
                onMouseOut={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}
              >{s.icon}</a>
            ))}
          </div>}
        </div>

        {/* Link columns */}
        <nav aria-label="Footer navigation" style={{
          flex: isMobile ? "none" : 1,
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(3, 1fr)",
          columnGap: isMobile ? 32 : 64,
          rowGap: isMobile ? 32 : 40,
        }}>
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 14, fontFamily: "var(--body)" }}>{col.title}</div>
              {col.links.map((l) => (
                <a key={l.label} href={l.href}
                  {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  style={{ display: "flex", alignItems: "center", gap: l.icon ? 8 : 0, fontSize: 14, color: "rgba(255,255,255,0.55)", marginBottom: 10, fontFamily: "var(--body)", transition: "color 0.2s", textDecoration: "none" }}
                  onMouseOver={(e) => e.currentTarget.style.color = TEXT}
                  onMouseOut={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}
                >{l.icon && <span style={{ display: "flex", flexShrink: 0 }}>{l.icon}</span>}{l.label}</a>
              ))}
            </div>
          ))}
        </nav>
      </div>

      {/* Bottom bar */}
      <div style={{
        borderTop: "1px solid rgba(255,255,255,0.04)",
        paddingTop: 20,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
      }}>
        <p style={{ fontFamily: "var(--body)", fontSize: 13, color: TEXT_MUTED, margin: 0 }}>
          &copy; 2026 AI-Advisors.ai
        </p>
        <p style={{ fontFamily: "var(--body)", fontSize: 13, color: TEXT_MUTED, margin: 0 }}>
          All rights reserved.
        </p>
      </div>
    </footer>
    <BrandBillboard isMobile={isMobile} />
    </>
  );
}
